import { z } from "zod";

/**
 * Shared remittance line shapes.
 *
 * These schemas travel unchanged across three steps —
 * `normalize_remittance_document` produces them, `create_pending_payment`
 * passes them through, and `resolve_invoice_references` consumes them — so they
 * live here rather than being redeclared in each step file. A field added to
 * one copy but not the others is silently dropped mid-pipeline, which is
 * exactly how `additionalIdentifier` would have been lost.
 */

/**
 * One invoice the customer claims to be paying, as stated on the remittance.
 *
 * Everything here is customer-asserted and unverified: `invoiceNumber` is
 * whatever string the document showed, which routinely fails to resolve (a
 * mangled number, another system's reference, a plain typo).
 */
export const invoiceReferenceSchema = z.object({
  invoiceNumber: z.string(),
  appliedAmount: z.number().optional(),
  grossAmount: z.number().optional(),
  discountAmount: z.number().optional(),
  adjustmentAmount: z.number().optional(),
  /**
   * A secondary identifier stated alongside the invoice number — in practice a
   * customer PO pulled out of the line's remarks ("PO 2192312"). It exists
   * because deduction lines are the ones most likely to carry a nonsense
   * invoice number while still naming a resolvable PO, which is the only way
   * to tie the deduction back to the invoice it was withheld from.
   *
   * Free-form and unparsed at this layer: the resolver tokenizes it rather than
   * assuming a format, since the extraction has no schema for what a given
   * customer writes in a remarks column.
   */
  additionalIdentifier: z.string().nullable().optional(),
});

/**
 * A standalone (general) deduction the customer withheld that isn't tied to a
 * specific invoice reference — e.g. a freight/shortage claim stated on the
 * remittance as a lump sum. Invoice-tied deductions live on the
 * `discountAmount`/`adjustmentAmount` of an invoice reference instead.
 */
export const deductionSchema = z.object({
  amount: z.number(),
  reason: z.string().optional(),
});

export type InvoiceReference = z.infer<typeof invoiceReferenceSchema>;
export type RemittanceDeduction = z.infer<typeof deductionSchema>;
